import React, { useState, useEffect } from 'react';
import { Alert } from '../../utils/Alert';
import { asList } from '../../utils/apiList';
import { Pagination, usePagination } from '../../components/ui/Pagination';
import axios from 'axios';
import MainLayout from '../../components/layout/MainLayout';
import AdminSidebar from './AdminSidebar';
import { FaPlus, FaSearch, FaBook, FaSync, FaTimes, FaTrash } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import './AdminUnitsPage.css';
import './Admin.css';


import { API_URL } from '../../config';
interface Unit {
  id: number;
  name: string;
  description: string;
  subject_id: number;
  created_by: number;
  created_at: string;
  updated_at: string | null;
}

interface FormData {
  name: string;
  description: string;
  subject_id: string;
}

// Add interfaces for hierarchical filtering
interface Class {
  id: number;
  name: string;
  description: string;
}

interface Stream {
  id: number;
  name: string;
  description: string;
  class_id: number;
  class_: Class;
}

interface Subject {
  id: number;
  name: string;
  code: string;
  description: string;
  stream_id: number;
  stream: Stream;
}

const AdminUnitsPage: React.FC = () => {
  const { token, logout } = useAuth();
  const [units, setUnits] = useState<Unit[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  // Add modal filter states
  const [modalSelectedClass, setModalSelectedClass] = useState<number>(0);
  const [modalSelectedStream, setModalSelectedStream] = useState<number>(0);
  const [modalFilteredStreams, setModalFilteredStreams] = useState<Stream[]>([]);
  const [modalFilteredSubjects, setModalFilteredSubjects] = useState<Subject[]>([]);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    subject_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  
  // Add notification states
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    show: boolean;
  } | null>(null);
  
  // Add delete confirmation state
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    unitId: number | null;
    unitName: string;
  }>({
    show: false,
    unitId: null,
    unitName: ''
  });
  
  // Add filter states
  const [selectedClass, setSelectedClass] = useState<number>(0);
  const [selectedStream, setSelectedStream] = useState<number>(0);
  const [selectedSubject, setSelectedSubject] = useState<number>(0);
  const [sortBy, setSortBy] = useState('name-asc');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [unitsResponse, subjectsResponse, streamsResponse, classesResponse] = await Promise.all([
          axios.get(`${API_URL}/api/units/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          axios.get(`${API_URL}/api/subjects/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          axios.get(`${API_URL}/api/streams/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }),
          axios.get(`${API_URL}/api/classes/`, {
            headers: {
              'accept': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          })
        ]);
        setUnits(asList(unitsResponse.data));
        setSubjects(asList(subjectsResponse.data));
        setStreams(asList(streamsResponse.data));
        setClasses(asList(classesResponse.data));
      } catch (err) {
        setMessage({ text: 'Failed to load data. Please try again.', type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [token]);

  React.useEffect(() => {
    if (message.text && message.type === 'success') {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear form errors when user starts typing
    if (formErrors[name]) {
      const updatedErrors = { ...formErrors };
      delete updatedErrors[name];
      setFormErrors(updatedErrors);
    }
    // Clear notification when user starts typing
    if (notification && notification.type === 'error') {
      setNotification(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ text: '', type: '' });
    try {
      const url = selectedUnit
        ? `${API_URL}/api/units/${selectedUnit.id}`
        : `${API_URL}/api/units/`;
      const method = selectedUnit ? 'PUT' : 'POST';
      const payload = {
        name: formData.name,
        description: formData.description,
        subject_id: Number(formData.subject_id)
      };
      const response = await axios({
        method,
        url,
        data: payload,
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 200 || response.status === 201) {
        const updatedUnit = response.data;
        setUnits(prevUnits =>
          selectedUnit
            ? prevUnits.map(unit => unit.id === updatedUnit.id ? updatedUnit : unit)
            : [...prevUnits, updatedUnit]
        );
        setOpenDialog(false);
        setSelectedUnit(null);
        resetForm();
        
        // Show success notification
        Alert.success('Success', selectedUnit ? 'Unit updated successfully' : 'Unit created successfully');
      } else {
        throw new Error('Failed to save unit');
      }
    } catch (err) {
      console.error('Error saving unit:', err);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          Alert.error('Error', 'Unauthorized: Please login again');
        } else if (err.response?.status === 403) {
          Alert.error('Error', 'Forbidden: You do not have permission to save units');
        } else if (err.response?.status === 404) {
          Alert.error('Error', 'Unit not found');
        } else if (err.response?.status === 409) {
          // Handle duplicate unit name validation error
          const errorMessage = err.response?.data?.detail || 'A unit with this name already exists for this subject. Please use a different name.';
          // Set form error for the name field (only in modal, not on main page)
          setFormErrors({ name: errorMessage });
        } else if (err.response?.data?.detail) {
          Alert.error('Error', err.response.data.detail);
        } else {
          Alert.error('Error', `Failed to save unit: ${err.response?.data?.message || err.message}`);
        }
      } else {
        Alert.error('Error', err instanceof Error ? err.message : 'Failed to save unit');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (unit: Unit) => {
    setSelectedUnit(unit);
    
    // Find the subject for this unit
    const subject = subjects.find(s => s.id === unit.subject_id);
    if (subject) {
      // Set the class and stream
      const stream = streams.find(s => s.id === subject.stream_id);
      if (stream) {
        // Set class
        setModalSelectedClass(stream.class_id);
        // Filter and set streams for this class
        const classStreams = streams.filter(s => s.class_id === stream.class_id);
        setModalFilteredStreams(classStreams);
        // Set stream
        setModalSelectedStream(stream.id);
        // Filter and set subjects for this stream
        const streamSubjects = subjects.filter(s => s.stream_id === stream.id);
        setModalFilteredSubjects(streamSubjects);
      }
    }

    setFormData({
      name: unit.name,
      description: unit.description || '',
      subject_id: String(unit.subject_id)
    });
    setOpenDialog(true);
  };

  const handleDelete = async (unitId: number) => {
    const confirmResult = await Alert.confirm('Are you sure?', 'Are you sure you want to delete this unit?');
    if (confirmResult.isConfirmed) {
      try {
        const response = await axios.delete(`${API_URL}/api/units/${unitId}`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.status === 204 || response.status === 307) {
          setUnits(prevUnits => prevUnits.filter(unit => unit.id !== unitId));
          setMessage({ text: 'Unit deleted successfully', type: 'success' });
        } else if (response.status === 200 && response.data && response.data.id) {
          setUnits(prevUnits => prevUnits.filter(unit => unit.id !== response.data.id));
          setMessage({ text: 'Unit deleted successfully', type: 'success' });
        } else {
          throw new Error('Failed to delete unit');
        }
      } catch (err) {
        setMessage({ text: err instanceof Error ? err.message : 'Failed to delete unit', type: 'error' });
      }
    }
  };

  // Handle modal class change
  const handleModalClassChange = (value: number) => {
    setModalSelectedClass(value);
    setModalSelectedStream(0);
    setFormData(prev => ({ ...prev, subject_id: '' }));
    if (value) {
      const filteredStreams = streams.filter(stream => stream.class_id === value);
      setModalFilteredStreams(filteredStreams);
    } else {
      setModalFilteredStreams([]);
    }
    setModalFilteredSubjects([]);
  };

  // Handle modal stream change
  const handleModalStreamChange = (value: number) => {
    setModalSelectedStream(value);
    setFormData(prev => ({ ...prev, subject_id: '' }));
    if (value) {
      const filteredSubjects = subjects.filter(subject => subject.stream_id === value);
      setModalFilteredSubjects(filteredSubjects);
    } else {
      setModalFilteredSubjects([]);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', subject_id: '' });
    setSelectedUnit(null);
    setFormErrors({});
    setModalSelectedClass(0);
    setModalSelectedStream(0);
    setModalFilteredStreams([]);
    setModalFilteredSubjects([]);
    // Clear notification when form is reset
    if (notification) {
      setNotification(null);
    }
  };

  // Filter handlers
  const handleClassChange = (value: number) => {
    setSelectedClass(value);
    setSelectedStream(0);
    setSelectedSubject(0);
    if (!value) {
      setSortBy('name-asc'); // Reset sort when "All Classes" is selected
    }
  };

  const handleStreamChange = (value: number) => {
    setSelectedStream(value);
    setSelectedSubject(0);
  };

  const handleSubjectChange = (value: number) => {
    setSelectedSubject(value);
  };

  // Show notification helper function
  const showNotification = (type: 'success' | 'error' | 'info', message: string) => {
    if (type === 'success') {
      Alert.success('Success!', message);
    } else if (type === 'error') {
      Alert.error('Oops...', message);
    } else {
      Alert.warning('Notice', message);
    }
  };
  
  // Handle delete confirmation
  const handleDeleteClick = (unit: Unit) => {
    setDeleteConfirmation({
      show: true,
      unitId: unit.id,
      unitName: unit.name
    });
  };
  
  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteConfirmation.unitId) return;
    
    try {
      const response = await axios.delete(`${API_URL}/api/units/${deleteConfirmation.unitId}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.status === 204 || response.status === 307) {
        setUnits(prevUnits => prevUnits.filter(unit => unit.id !== deleteConfirmation.unitId));
        Alert.success('Success', `Unit "${deleteConfirmation.unitName}" deleted successfully`);
      } else if (response.status === 200 && response.data && response.data.id) {
        setUnits(prevUnits => prevUnits.filter(unit => unit.id !== response.data.id));
        Alert.success('Success', `Unit "${deleteConfirmation.unitName}" deleted successfully`);
      } else {
        throw new Error('Failed to delete unit');
      }
      
      // Close confirmation dialog
      setDeleteConfirmation({ show: false, unitId: null, unitName: '' });

    } catch (err) {
      console.error('Error deleting unit:', err);
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          Alert.error('Error', 'Unauthorized: Please login again');
        } else if (err.response?.status === 403) {
          Alert.error('Error', 'Forbidden: You do not have permission to delete units');
        } else if (err.response?.status === 404) {
          Alert.error('Error', 'Unit not found');
        } else if (err.response?.data?.detail) {
          Alert.error('Error', err.response.data.detail);
        } else {
          Alert.error('Error', `Failed to delete unit: ${err.response?.data?.message || err.message}`);
        }
      } else {
        Alert.error('Error', 'Failed to delete unit');
      }
    }
  };
  
  // Cancel delete
  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, unitId: null, unitName: '' });
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedClass(0);
    setSelectedStream(0);
    setSelectedSubject(0);
    setSortBy('name-asc');
  };

  // Filtered data
  const filteredStreams = selectedClass ? streams.filter(stream => stream.class_id === selectedClass) : streams;
  const filteredSubjects = selectedStream ? subjects.filter(subject => subject.stream_id === selectedStream) : subjects;

  // Filter and sort units
  const filteredAndSortedUnits = React.useMemo(() => {
    let result = [...units];
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter(unit => 
        unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (unit.description && unit.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by class (through subjects -> streams -> classes)
    if (selectedClass) {
      result = result.filter(unit => {
        const subject = subjects.find(s => s.id === unit.subject_id);
        return subject?.stream?.class_?.id === selectedClass;
      });
    }
    
    // Filter by stream (through subjects -> streams)
    if (selectedStream) {
      result = result.filter(unit => {
        const subject = subjects.find(s => s.id === unit.subject_id);
        return subject?.stream?.id === selectedStream;
      });
    }
    
    // Filter by subject
    if (selectedSubject) {
      result = result.filter(unit => unit.subject_id === selectedSubject);
    }
    
    // Sort only when a class is selected
    if (selectedClass) {
      switch (sortBy) {
        case 'name-asc':
          result.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'name-desc':
          result.sort((a, b) => b.name.localeCompare(a.name));
          break;
        case 'date-asc':
          result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
          break;
        case 'date-desc':
          result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
          break;
        default:
          break;
      }
    }
    
    return result;
  }, [units, searchTerm, sortBy, selectedClass, selectedStream, selectedSubject, subjects]);

  const pagination = usePagination(filteredAndSortedUnits, 50);

  return (
    <MainLayout user={{ name: '', role: 'admin' }} onLogout={logout} sidebarContent={<AdminSidebar />}> 
      <div className="subject-page-root">
        <div className="px-8 py-6 flex flex-col items-start">
          <div className="flex items-center justify-between w-full ">
            <div className="flex items-center">
            <h1 className="admin-title">Manage Units</h1>
            </div>
            <button
              onClick={() => setOpenDialog(true)}
              className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-[#111827] hover:bg-gray-800 text-white font-kumbh text-base font-semibold shadow-lg transition active:scale-95 min-w-[160px]"
              style={{ boxShadow: '0 8px 24px #0050FF22' }}
              disabled={isLoading}
            >
              Add Unit
            </button>
          </div>
        </div>
        <div className="w-full flex justify-center mt-4 ">
          <div className="bg-white rounded-lg flex flex-col md:flex-row items-center justify-between p-6 w-full max-w-7xl gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0 w-full md:w-auto">
              <div className="relative min-w-[240px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {FaSearch({})}
                </span>
                <input
                  type="text"
                  placeholder="Search units.."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 shadow focus:ring-2 focus:ring-blue-200 focus:border-blue-400 bg-white text-sm w-full font-kumbh placeholder-gray-400"
                  style={{ boxShadow: '0 2px 6px #0001' }}
                />
              </div>
             
              <select
                value={selectedClass}
                onChange={e => handleClassChange(Number(e.target.value))}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px]"
                style={{ boxShadow: '0 2px 6px #0001' }}
              >
                <option value={0}>All Classes</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
              <select
                value={selectedStream}
                onChange={e => handleStreamChange(Number(e.target.value))}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px]"
                style={{ boxShadow: '0 2px 6px #0001' }}
                disabled={!selectedClass}
              >
                <option value={0}>All Streams</option>
                {filteredStreams.map(stream => (
                  <option key={stream.id} value={stream.id}>{stream.name}</option>
                ))}
              </select>
              <select
                value={selectedSubject}
                onChange={e => handleSubjectChange(Number(e.target.value))}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px]"
                style={{ boxShadow: '0 2px 6px #0001' }}
                disabled={!selectedStream}
              >
                <option value={0}>All Subjects</option>
                {filteredSubjects.map(subject => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>
              {/* <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px]"
                style={{ boxShadow: '0 2px 6px #0001' }}
                disabled={!selectedSubject}
              >
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="date-asc">Date (Oldest first)</option>
                <option value="date-desc">Date (Newest first)</option>
              </select>
              {(searchTerm || selectedClass || selectedStream || selectedSubject || sortBy !== 'name-asc') && (
            <button
                  onClick={handleResetFilters}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-600 font-kumbh shadow hover:bg-gray-50 active:scale-95 transition"
                  style={{ boxShadow: '0 2px 6px #0001' }}
              disabled={isLoading}
            >
                  {FaSync({ className: 'h-4 w-4' })}
                  Reset Filters
            </button>
              )} */}
               <button
              onClick={handleResetFilters}
                className="rounded-lg border border-gray-200 py-2.5 px-3 text-sm font-kumbh bg-white focus:ring-2 focus:ring-blue-200 focus:border-blue-400 shadow min-w-[120px] font-medium transition active:scale-95"
                style={{ fontFamily: 'Inter, sans-serif', boxShadow: '0 2px 6px #0001' }}
            >
              Reset Filters
            </button>
            </div>
           
          </div>
        </div>
        <div className="flex-grow overflow-auto p-8 pt-2">
          <div className="max-w-7xl mx-auto">
            {message.text && (
              <div className={`mb-4 p-4 rounded-md ${
                message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
              }`}>
                {message.text}
              </div>
            )}
            {isLoading && !openDialog && (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
            {!isLoading && (
              <div className="bg-white shadow rounded-lg overflow-x-auto mt-3">
                <table className="min-w-full divide-y divide-gray-100 font-kumbh">
                  <thead className="bg-white">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[30%]">NAME</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[30%]">DESCRIPTION</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[20%]">SUBJECT</th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wider w-[12%]">ACTION</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {pagination.paginatedData.map((unit) => {
                      const subject = subjects.find(s => s.id === unit.subject_id);
                      return (
                        <tr key={unit.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                            <div className="truncate max-w-[160px] mx-auto" title={unit.name}>{unit.name}</div>
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-600 text-center">
                            <div className="truncate max-w-[200px] mx-auto" title={unit.description || 'desc'}>{unit.description || ''}</div>
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-600 text-center">
                            <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 truncate max-w-[120px]" title={subject?.name || 'No Subject'}>
                              {subject?.name || 'No Subject'}
                            </span>
                          </td>
                         
                          <td className="px-6 py-3 whitespace-nowrap text-center text-sm font-medium">
                            <div className="flex justify-center items-center gap-3 min-w-[140px]">
                              <button
                                onClick={() => handleEdit(unit)}
                                className="inline-flex items-center px-3 py-1.5 mr-3 text-sm font-medium text-[#111827] bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2 transition-colors"
                                disabled={isLoading}
                              >
                                <span className="w-4 h-4 mr-1.5">
                                  <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </span>
                                Edit
                              </button>
                              <button 
                                onClick={() => handleDeleteClick(unit)}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                                disabled={isLoading}
                              >
                                <span className="w-4 h-4 mr-1.5">
                                  <svg fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </span>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredAndSortedUnits.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-8 py-4 text-center text-sm text-gray-400 font-kumbh">
                          {searchTerm ? 'No units match your search criteria.' : 'No units available. Add your first unit!'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <Pagination {...pagination} onPageChange={pagination.setCurrentPage} onItemsPerPageChange={pagination.setItemsPerPage} />
              </div>
            )}
          </div>
        </div>
        {openDialog && (
          <div className="subject-modal-overlay">
            <div className="subject-modal-box" style={{ display: 'flex', flexDirection: 'column', maxHeight: '85vh', width: '800px', maxWidth: '90vw' }}>
              {/* Modal Header - Fixed */}
              <div className="subject-modal-title" style={{ flex: 'none', borderBottom: '1px solid #eee', paddingBottom: 12, marginBottom: 0 }}>{selectedUnit ? 'Edit Unit' : 'Add Unit'}</div>
              {/* Modal Body - Scrollable */}
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 0', marginBottom: 0 }}>
                  <div className="grid grid-cols-2 gap-6 px-4">
                    <div>
                      <label htmlFor="modal-class" className="subject-modal-form-label">Class*</label>
                      <select
                        id="modal-class"
                        value={modalSelectedClass}
                        onChange={(e) => handleModalClassChange(Number(e.target.value))}
                        className="subject-modal-input"
                        required
                      >
                        <option value={0}>Select a class</option>
                        {classes.map(cls => (
                          <option key={cls.id} value={cls.id}>{cls.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="modal-stream" className="subject-modal-form-label">Stream*</label>
                      <select
                        id="modal-stream"
                        value={modalSelectedStream}
                        onChange={(e) => handleModalStreamChange(Number(e.target.value))}
                        className="subject-modal-input"
                        required
                        disabled={!modalSelectedClass}
                      >
                        <option value={0}>Select a stream</option>
                        {modalFilteredStreams.map(stream => (
                          <option key={stream.id} value={stream.id}>{stream.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="new-subject" className="subject-modal-form-label">Subject*</label>
                      <select
                        id="new-subject"
                        name="subject_id"
                        value={formData.subject_id}
                        onChange={handleInputChange}
                        className="subject-modal-input"
                        required
                        disabled={!modalSelectedStream}
                      >
                        <option value="">Select a subject</option>
                        {modalFilteredSubjects.map(subject => (
                          <option key={subject.id} value={subject.id}>{subject.name}</option>
                        ))}
                      </select>
                      {formErrors.subject_id && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.subject_id}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="new-name" className="subject-modal-form-label">Unit Name*</label>
                      <input
                        type="text"
                        id="new-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="subject-modal-input"
                        placeholder="Enter unit name"
                        required
                      />
                      {formErrors.name && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                      )}
                    </div>

                    <div className="col-span-2">
                      <label htmlFor="new-description" className="subject-modal-form-label">Description</label>
                      <textarea
                        id="new-description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={3}
                        className="subject-modal-textarea"
                        placeholder="Enter description"
                      />
                    </div>
                  </div>
                  <div className="subject-modal-helper px-4 mt-4">Fields marked with * are required</div>
                </div>
                {/* Modal Footer - Fixed */}
                <div className="subject-modal-actions" style={{ flex: 'none', borderTop: '1px solid #eee', paddingTop: 12, background: '#fff' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDialog(false);
                      resetForm();
                    }}
                    className="subject-modal-cancel-btn"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="subject-modal-save-btn transition"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Saving...' : (selectedUnit ? 'Save Changes' : 'Save Unit')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Notification Popup */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 max-w-sm">
          <div className={`rounded-lg shadow-lg p-4 border-l-4 ${
            notification.type === 'success' 
              ? 'bg-green-50 border-green-400 text-green-800' 
              : notification.type === 'error'
              ? 'bg-red-50 border-red-400 text-red-800'
              : 'bg-blue-50 border-blue-400 text-blue-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-2 h-2 rounded-full mr-3 ${
                  notification.type === 'success' ? 'bg-green-400' 
                  : notification.type === 'error' ? 'bg-red-400' 
                  : 'bg-blue-400'
                }`}></div>
                <p className="text-sm font-medium">{notification.message}</p>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="ml-4 text-gray-400 hover:text-gray-600"
              >
                {FaTimes({ className: "w-4 h-4" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  {FaTrash({ className: "h-6 w-6 text-red-600" })}
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Delete Unit
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      Are you sure you want to delete the unit "{deleteConfirmation.unitName}"? This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition active:scale-95"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700 sm:mt-0 sm:w-auto sm:text-sm transition active:scale-95"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AdminUnitsPage; 
